import { whenOnce } from "@arcgis/core/core/reactiveUtils";
import { getMarkerPopupHorizontalPlacement, getMarkerPopupPlacement } from "@components/world-map/world-map-utils";

/** Vertical and horizontal popup placement relative to a marker. */

/** Amount to increase zoom when clicking into a cluster. */
const CLUSTER_ZOOM_INCREASE = 3;

/**
 * Computes vertical and horizontal popup placement for a marker
 * given its screen position and the map view dimensions.
 */
export function getPopupPlacement(screenPoint, viewWidth, viewHeight) {
  const placement = getMarkerPopupPlacement(screenPoint.y, viewHeight);
  if (placement === "center") {
    return {
      placement,
      horizontalPlacement: screenPoint.x <= viewWidth / 2 ? "right" : "left"
    };
  }
  return {
    placement,
    horizontalPlacement: getMarkerPopupHorizontalPlacement(screenPoint.x, viewWidth)
  };
}

/**
 * Hit-tests the map at the given event position and returns the first layer graphic,
 * regardless of whether it is a cluster or an individual marker.
 */
export async function getAnyLayerGraphic(view, layer, event) {
  const hitTest = layer ? await view.hitTest(event, {
    include: [layer]
  }) : await view.hitTest(event);
  const graphicResult = hitTest.results.find(r => r.type === "graphic");
  return graphicResult?.type === "graphic" ? graphicResult.graphic : null;
}

/**
 * Zooms into a cluster graphic center to reveal individual markers.
 * Waits for the view to settle, then refreshes the layer to clear cached cluster graphics.
 */
export async function zoomIntoCluster(view, layer, clusterGraphic) {
  await view.goTo({
    center: clusterGraphic.geometry,
    zoom: view.zoom + CLUSTER_ZOOM_INCREASE
  });

  // Wait until the view is fully settled after the zoom animation.
  if (!view.stationary) {
    await whenOnce(() => view.stationary);
  }

  // Force the layer to re-fetch its data and recalculate clustering from scratch.
  // Without this, ArcGIS sometimes caches the old cluster graphics and doesn't
  // re-evaluate them after a programmatic goTo.
  layer.refresh();
}